import { ROUTES } from 'constant/routes';

import { normalizePathname } from './normalizePathname';

const STORAGE_KEY = 'rxl_post_auth_return_url';
/** Whole path+search must stay under this (email/SMS tracking query strings included). */
const MAX_LEN = 2048;

function blockedPrefixes(): string[] {
  return [
    ROUTES.login,
    ROUTES.forgotPassword,
    ROUTES.createAccount,
    ROUTES.createPassword,
    ROUTES.existingAccount,
    ROUTES.guestCheckout,
    ROUTES.referral,
    ROUTES.influencer
  ].sort((a, b) => b.length - a.length);
}

function isBlockedPath(pathOnly: string): boolean {
  if (pathOnly.startsWith('/api')) {
    return true;
  }
  return blockedPrefixes().some(
    (prefix) => pathOnly === prefix || pathOnly.startsWith(`${prefix}/`)
  );
}

/**
 * Returns a safe internal path+search for post-login redirect, or null.
 * Rejects open redirects, auth flows, and malformed values.
 *
 * Opaque query keys (e.g. mail/SMS tracking `trk_id`, `hash_id`) are kept as-is on
 * the path segment — only the pathname is validated against blocked prefixes; the
 * search string is not interpreted beyond length and delimiter safety.
 */
export function sanitizePostAuthReturnUrl(raw: string): string | null {
  if (typeof raw !== 'string') {
    return null;
  }

  let candidate = raw.trim().slice(0, MAX_LEN);
  if (!candidate) {
    return null;
  }

  for (let i = 0; i < 3 && candidate.includes('%'); i += 1) {
    try {
      candidate = decodeURIComponent(candidate);
    } catch {
      return null;
    }
  }

  candidate = candidate.trim().slice(0, MAX_LEN);
  if (!candidate || candidate.length > MAX_LEN) {
    return null;
  }

  const scriptScheme = `${'java'}${'script'}:`;
  if (
    candidate.includes('://') ||
    candidate.includes('\\') ||
    candidate.includes('<') ||
    candidate.includes('>') ||
    candidate.includes('\n') ||
    candidate.includes('\r') ||
    candidate.toLowerCase().includes(scriptScheme)
  ) {
    return null;
  }

  if (!candidate.startsWith('/')) {
    return null;
  }

  const [pathPart, ...rest] = candidate.split('?');
  const pathOnly = normalizePathname(pathPart ?? '');

  if (!pathOnly.startsWith('/')) {
    return null;
  }

  if (isBlockedPath(pathOnly)) {
    return null;
  }

  const query = rest.length ? `?${rest.join('?')}` : '';
  const rebuilt = `${pathOnly}${query}`;

  if (rebuilt.length > MAX_LEN) {
    return null;
  }

  return rebuilt;
}

export function rememberCurrentPageForPostAuthLogin(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const full = `${window.location.pathname}${window.location.search}`;
    const sanitized = sanitizePostAuthReturnUrl(full);
    if (sanitized) {
      sessionStorage.setItem(STORAGE_KEY, sanitized);
    }
  } catch {
    // ignore quota / private mode
  }
}

export function peekPostAuthReturnUrl(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? sanitizePostAuthReturnUrl(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Returns a safe redirect target and schedules clearing sessionStorage after
 * the current synchronous work so React Strict Mode double renders still
 * read the same value.
 */
export function takePostAuthReturnUrl(): string | null {
  const next = peekPostAuthReturnUrl();
  if (next) {
    queueMicrotask(() => {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    });
  }
  return next;
}

export function clearPostAuthReturnUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Login URL with a safe `next` query param so return survives remounts (e.g. React Strict Mode).
 * The full current search string (tracking params on the deep link) is encoded inside `next`.
 */
export function buildLoginUrlWithReturnPath(
  loginPath: string,
  pathnameWithSearch: string
): string {
  const sanitized = sanitizePostAuthReturnUrl(pathnameWithSearch);
  if (!sanitized) {
    return loginPath;
  }
  const sep = loginPath.includes('?') ? '&' : '?';
  return `${loginPath}${sep}next=${encodeURIComponent(sanitized)}`;
}

/** Maps `/` and other non-actionable paths to the authenticated home route. */
export function resolvePostAuthReturnUrl(raw: string): string {
  const sanitized = sanitizePostAuthReturnUrl(raw);

  if (!sanitized || sanitized === '/') {
    return ROUTES.dashboard;
  }

  return sanitized;
}
